import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * ScrollToStudioHint
 * ----------------------------------------------------------------------------
 * Floating pill anchored to the VIEWPORT (not any section), so it remains
 * visible while users read the hero or features without needing to scroll
 * past them first.
 *
 *  - Appears ~500ms after mount (subtle, not jumpy).
 *  - Auto-hides when the Resume Studio section intersects the viewport at
 *    30%+ visibility — the hint's job is done, it gets out of the way.
 *  - Dismissible via the × button (per-session, localStorage).
 *  - Click the main body → smooth-scroll to #resume-studio + hide.
 *  - `bottom-28` clears the CookieHub consent banner on first load.
 *  - Keyboard-accessible, aria-labelled, reduced-motion safe.
 * ----------------------------------------------------------------------------
 */

const DISMISS_KEY = 'ascend_studio_hint_dismissed_v1';

const ScrollToStudioHint = () => {
  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
  });
  const rootRef = useRef(null);

  // Delayed entrance so it doesn't compete with the hero on first paint.
  useEffect(() => {
    if (hidden) return;
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, [hidden]);

  // Auto-hide once the user reaches the Resume Studio section.
  useEffect(() => {
    if (hidden) return;
    const target = document.getElementById('resume-studio');
    if (!target || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            setVisible(false);
            io.disconnect();
          }
        }
      },
      { threshold: [0, 0.3, 0.6, 1] },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [hidden]);

  const handleClick = (e) => {
    e.preventDefault();
    const target = document.getElementById('resume-studio');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.location.hash = '#resume-studio';
    }
    setVisible(false);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setVisible(false);
    setHidden(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* storage disabled */ }
  };

  if (hidden) return null;

  return (
    <div
      ref={rootRef}
      className={`scroll-studio-hint fixed right-4 sm:right-6 bottom-24 sm:bottom-28 z-[55] transition-all duration-500 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
      data-testid="scroll-to-studio-hint"
      aria-hidden={!visible}
    >
      <button
        type="button"
        onClick={handleClick}
        className="group flex items-center gap-3 pl-4 pr-2 py-2.5 bg-[#0f1115] text-white shadow-[0_10px_30px_-8px_rgba(0,47,167,0.45),0_4px_12px_-4px_rgba(15,17,21,0.35)] hover:bg-[#002fa7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002fa7] focus-visible:ring-offset-2"
        aria-label="Scroll to Resume Studio showcase"
        data-testid="scroll-to-studio-hint-cta"
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white/10 group-hover:bg-white/20">
          <ChevronDown
            size={16}
            className="studio-hint-chevron text-white"
            aria-hidden="true"
          />
        </span>
        <span className="text-sm font-semibold whitespace-nowrap pr-2">
          See Resume Studio
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={handleDismiss}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDismiss(e); }
          }}
          className="flex items-center justify-center w-6 h-6 ml-1 -mr-1 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          aria-label="Dismiss"
          data-testid="scroll-to-studio-hint-dismiss"
        >
          <X size={13} />
        </span>
      </button>

      <style>{`
        @keyframes studioHintBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(3px); }
        }
        .studio-hint-chevron { animation: studioHintBounce 1.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .studio-hint-chevron { animation: none; }
          .scroll-studio-hint { transition: opacity 0.15s linear !important; }
        }
      `}</style>
    </div>
  );
};

export default ScrollToStudioHint;
